import {
  Box,
  Skeleton, Stack, Typography,
} from '@mui/material';
import React from 'react';
import {
  BackButton,
  getFormattedDateLongMonthYYYY,
  ContextMenuItem,
  ContextMenuButton,
  ContextMenu,
  TravelerTagChips,
  OtherChips,
} from '@marageti/z4-lib';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { User, UserStatus } from '@marageti/z4-sdk/lib/auth';
import SubNavigation from '../sub-navigation';
import { TabItem } from '../sub-navigation/types';
import { StatusItemProps } from '../agent-sub-navigation/types';
import { peopleStatusIcons } from '../status-icons';

const { getTravelerTagChip } = TravelerTagChips;
const { ChipRepeat } = OtherChips;

type ViewProps = {
  traveler: Traveler | undefined;
  user: User | undefined;
  tabs: TabItem[]
  contextMenuAnchor: HTMLElement | null;
  contextMenuOpen: boolean;
  onClickNewStatus: (status: UserStatus) => void;
  setContextMenuAnchor: (e: HTMLElement | null) => void;
};

const getHistory = (traveler: Traveler | undefined): string | undefined => {
  if (!traveler) return undefined;
  if (!traveler.createdAt) return '-';
  return getFormattedDateLongMonthYYYY(traveler.createdAt);
};

const StatusItem = ({ onClick, status }: StatusItemProps) => (
  <ContextMenuItem onClick={() => onClick(status)}>
    <Stack spacing={1.5} direction="row">
      {peopleStatusIcons[status].icon}
      <Typography variant="h4">
        {peopleStatusIcons[status].displayName}
      </Typography>
    </Stack>
  </ContextMenuItem>
);

const TravelerSubNavigationView = ({
  traveler,
  user,
  tabs,
  contextMenuAnchor,
  contextMenuOpen,
  onClickNewStatus,
  setContextMenuAnchor,
}: ViewProps) => (
  <SubNavigation
    profile={{
      primary: traveler?.fullName ? (
        <Stack component="span" direction="row" alignItems="center" spacing={1}>
          <Typography
            component="span"
            variant="h3"
            fontWeight="bold"
          >
            {traveler?.fullName}
          </Typography>
          <Box
            component="span"
            display="flex"
          >
            {user?.status && (
            <ContextMenuButton
              id="traveler-status-context-button-id"
              onClick={(e) => setContextMenuAnchor(e.currentTarget)}
              open={contextMenuOpen}
              menuId="traveler-status-context-button-menu-id"
              size="small"
            >
              {peopleStatusIcons[user.status].icon}
            </ContextMenuButton>
            )}
            <ContextMenu
              anchorEl={contextMenuAnchor}
              buttonId="traveler-status-context-menu-button-id"
              id="traveler-status-context-menu-id"
              onClose={() => setContextMenuAnchor(null)}
              open={contextMenuOpen}
            >
              { (Object.keys(UserStatus) as Array<keyof typeof UserStatus>).map((key) => (
                <StatusItem
                  key={key}
                  onClick={onClickNewStatus}
                  status={key as UserStatus}
                />
              ))}
            </ContextMenu>
          </Box>
        </Stack>
      ) : <Skeleton width={250} height={34} />,
      secondary: traveler ? `Traveler since ${getHistory(traveler) || ''}` : <Skeleton width={150} />,
      decoration: (
        <Stack direction="row" spacing={0.75}>
          {traveler?.tags?.map((tag) => getTravelerTagChip(tag, 'small'))}
          {!!traveler?.totalZicassoTrips && <ChipRepeat />}
        </Stack>
      ),
    }}
    startAdornment={(
      <BackButton
        aria-label="back to travelers"
        defaultPath="/travelers"
        edge="start"
      />
    )}
    tabs={tabs}
  />
);

export default TravelerSubNavigationView;
